// Panzergrau look: textures generated once as SVG data URLs and handed to the CSS as variables
// (`--tex-grain`, `--tex-chips`, `--tex-camo-white`, `--tex-camo-olive`, `--tex-camo-red`). No image files.

const svgUrl = (svg: string) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

/** Seeded random, so the textures look the same on every load. */
export function rng(seed: number): () => number {
  return () => (seed = (seed * 1664525 + 1013904223) >>> 0) / 2 ** 32;
}

/** Seamless camo tile: blobs in `colors[1..]` on `colors[0]`. */
function camo(colors: string[], size: number, seed: number): string {
  const rand = rng(seed);
  let body = `<rect width="${size}" height="${size}" fill="${colors[0]}"/>`;
  colors.slice(1).forEach((color, layer) => {
    let paths = '';
    for (let n = 0; n < 7 - layer; n++) {
      const [cx, cy] = [rand() * size, rand() * size];
      const r = size * (0.09 + rand() * 0.08) * (1 - layer * 0.12);
      const k = 7;
      const pts: [number, number][] = [];
      for (let i = 0; i < k; i++) {
        const a = (i / k) * Math.PI * 2 + rand() * 0.4;
        const rr = r * (0.55 + rand() * 0.6) * (i % 2 ? 1.5 : 1);
        pts.push([Math.cos(a) * rr, Math.sin(a) * rr * 0.6]);
      }
      const mid = (p: number[], q: number[]) => `${((p[0] + q[0]) / 2).toFixed(1)} ${((p[1] + q[1]) / 2).toFixed(1)}`;
      let d = `M${mid(pts[k - 1], pts[0])}`;
      for (let i = 0; i < k; i++) d += `Q${pts[i][0].toFixed(1)} ${pts[i][1].toFixed(1)} ${mid(pts[i], pts[(i + 1) % k])}`;
      const rot = Math.round(rand() * 60 - 30);
      // Copies around the tile make it seamless.
      for (const dx of [-size, 0, size]) {
        for (const dy of [-size, 0, size]) {
          paths += `<path d="${d}Z" transform="translate(${(cx + dx).toFixed(1)} ${(cy + dy).toFixed(1)}) rotate(${rot})"/>`;
        }
      }
    }
    body += `<g fill="${color}">${paths}</g>`;
  });
  return svgUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">${body}</svg>`);
}

/** Noise tile in colour `rgb` (0..1) where the noise is above about `cut`; `gain` sharpens the edge. */
function noise(freq: number, octaves: number, seed: number, rgb: number[], gain: number, cut: number): string {
  const [r, g, b] = rgb;
  return svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256">` +
      `<filter id="f" x="0" y="0" width="100%" height="100%">` +
      `<feTurbulence type="fractalNoise" baseFrequency="${freq}" numOctaves="${octaves}" seed="${seed}" stitchTiles="stitch"/>` +
      `<feColorMatrix type="matrix" values="0 0 0 0 ${r} 0 0 0 0 ${g} 0 0 0 0 ${b} ${gain} 0 0 0 ${-gain * cut}"/>` +
      `</filter><rect width="100%" height="100%" filter="url(#f)"/></svg>`,
  );
}

export function applyTheme(): void {
  const set = (name: string, value: string) => document.documentElement.style.setProperty(name, value);
  set('--tex-grain', noise(0.85, 2, 4, [0, 0, 0], 0.6, 0.25));
  set('--tex-chips', noise(0.09, 4, 13, [0.72, 0.74, 0.74], 9, 0.66)); // paint chipped off to bare steel
  set('--tex-camo-white', camo(['#f4f6f7', '#c9ced1', '#8e979c', '#4b5358'], 240, 7));
  set('--tex-camo-olive', camo(['#4b5532', '#3a4226', '#5f6a3f', '#23281a'], 320, 3));
  set('--tex-camo-red', camo(['#5c1712', '#43100c', '#74241b', '#2a0906'], 120, 5)); // kick button
}
